import { Flex, Text } from '@chakra-ui/react'
import { RefreshCw } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DateTimeRangeDropdown } from '~/components/shared/DateTimeRangeDropdown'
import { getFilterItemKey } from '~/components/shared/filterSelection'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { DateRange, SelectedFilterData } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

import { FilterPills } from './components/FilterPills'
import { MissedCallsFilterButton } from './MissedCallsFilterButton'

interface MissedCallsListViewHeaderProps {
  dateTimeRange?: DateRange
  callCount: number
  tableSize: Size
  activeFilters?: SelectedFilterData[]
  onActiveFiltersChanged?: (activeFilters: SelectedFilterData[]) => void
  onDateTimeRangeChanged: (dateTimeRange: DateRange) => void
  onRefresh?: () => void
  isRefreshing?: boolean
}

const REFRESH_LOCKOUT_MS = 5000 // 5 seconds

export const MissedCallsListViewHeader = ({
  dateTimeRange,
  callCount = 0,
  tableSize,
  activeFilters = [],
  onActiveFiltersChanged,
  onDateTimeRangeChanged,
  onRefresh,
  isRefreshing = false,
}: MissedCallsListViewHeaderProps) => {
  const { t } = useTranslation()
  const lastRefreshTimeRef = useRef<number>(0)
  const [isRefreshLocked, setIsRefreshLocked] = useState(false)

  const handleRemoveFilter = (filterToRemove: SelectedFilterData) => {
    const keyToRemove = getFilterItemKey(
      filterToRemove.sectionTitle,
      filterToRemove.rowTitle,
      filterToRemove.item,
    )
    const updatedFilters = activeFilters.filter(
      (filter) =>
        getFilterItemKey(filter.sectionTitle, filter.rowTitle, filter.item) !==
        keyToRemove,
    )
    onActiveFiltersChanged?.(updatedFilters)
  }

  const handleClearAllFilters = () => {
    onActiveFiltersChanged?.([])
  }

  const handleRefresh = () => {
    const now = Date.now()
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current

    if (timeSinceLastRefresh < REFRESH_LOCKOUT_MS) {
      return // Prevent refresh if within lockout period
    }

    lastRefreshTimeRef.current = now
    setIsRefreshLocked(true)
    onRefresh?.()

    // Reset lockout after the period expires
    setTimeout(() => {
      setIsRefreshLocked(false)
    }, REFRESH_LOCKOUT_MS)
  }

  return (
    <Flex
      minHeight="4.5rem"
      height="4.5rem"
      px="1.25rem"
      alignItems="center"
      gap="0.75rem"
      alignSelf="stretch"
      borderBottom="2px solid"
      bg="bg.default"
      borderColor="border.subtle"
    >
      <Flex
        data-testid="input-wrapper"
        alignItems="center"
        gap="0.75rem"
        flex="1"
        flexWrap="wrap"
      >
        <DateTimeRangeDropdown
          onValueChange={onDateTimeRangeChanged}
          useQuickActionButton={tableSize === 'sm'}
          dateTimeRange={dateTimeRange}
        />
        <MissedCallsFilterButton
          activeFilters={activeFilters}
          onActiveFiltersChanged={onActiveFiltersChanged}
          useQuickActionButton={tableSize === 'sm'}
        />
        <FilterPills
          activeFilters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAllFilters={handleClearAllFilters}
        />
        <Text {...textStyles[textStyleKeys.Body2]} color="fg.default">
          {t('missedCalls.countLabel', { count: callCount })}
        </Text>
      </Flex>
      {onRefresh && (
        <Flex alignItems="flex-end">
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_200}
            tooltipText={t('missedCalls.refresh')}
            actionIcon={RefreshCw}
            onClick={handleRefresh}
            loading={isRefreshing}
            disabled={isRefreshLocked || isRefreshing}
          />
        </Flex>
      )}
    </Flex>
  )
}
